import { cn } from '@lair-finance/util'
import { forwardRef, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { MobileMenuList } from '@/domains/header/config'
import { Dialog, DialogClose, DialogPortal, DialogTrigger } from '@/shared/ui/Dialog'
import { NotificationBar } from '@/domains/header/components/Header/NotificationBar.tsx'
import { HeaderContent } from '@/domains/header/components/Header/HeaderContent.tsx'
import { ToggleButton } from '@/domains/header/components/Header/HeaderActions/ToggleButton.tsx'
import { Footer } from '@/shared/ui/Footer'
import { useModalControlStore } from '@/domains/header/model/store.ts'
import { useResponsive } from '@/shared/hooks/useResponsive.ts'
import { useChainId } from '@lair-finance/wagmi'
import { AssetIcon } from '@/shared/ui/Asset'

export function MenuButton() {
  const isDesktop = useResponsive('desktop')
  const [activeModal, openModal, closeModal] = useModalControlStore(state => [state.activeModal, state.openModal, state.closeModal])

  const content = (
    <div className={'fixed inset-0 w-screen h-screen flex flex-col z-50 overflow-hidden'}>
      <NotificationBar className={'flex-shrink-0'} />
      <HeaderContent className={'flex-shrink-0'} actions={<ToggleButton active={activeModal !== null} onClick={() => closeModal()} />} />
      <MenuModalContent />
      <Footer className={'flex-shrink-0'} />
    </div>
  )

  return (
    <Dialog
      open={activeModal === 'menu'}
      onOpenChange={open => {
        if (open) {
          openModal(activeModal === null ? 'menu' : null)
        } else {
          openModal(null)
        }
      }}
    >
      {!isDesktop && (
        <DialogTrigger>
          <ToggleButton active={activeModal !== null} />
        </DialogTrigger>
      )}
      <DialogPortal>{content}</DialogPortal>
    </Dialog>
  )
}

export const MenuModalContent = forwardRef<HTMLDivElement, { className?: string }>(({ className }, ref) => {
  const location = useLocation()
  const chainId = useChainId()
  const menuList = useMemo(() => MobileMenuList[chainId], [chainId])

  return (
    <div
      className={cn(
        'relative inline-flex flex-col w-screen h-full bg-greyscale-700 p-5 sm:px-6 overflow-x-hidden overflow-y-auto invisible-scrollbar',
        className,
      )}
      ref={ref}
    >
      {menuList.map(item => {
        const active = item.isActive(location.pathname)

        if (item.isExternal) {
          return (
            <DialogClose key={item.id} asChild>
              <a className={cn('group flex', className)} href={item.path} target={'_blank'}>
                <MenuItem active={active} isNew={item.isNew}>
                  {item.name}
                  <AssetIcon filename={'new-external.png'} size={24} className={'group-hover:hidden'} />
                  <AssetIcon filename={'new-external-white.png'} size={24} className={'hidden group-hover:inline-block'} />
                </MenuItem>
              </a>
            </DialogClose>
          )
        }

        return (
          <DialogClose key={item.id} asChild>
            <Link className={cn('flex', className)} to={item.path}>
              <MenuItem active={active} isNew={item.isNew}>
                {item.name}
              </MenuItem>
            </Link>
          </DialogClose>
        )
      })}
    </div>
  )
})
MenuModalContent.displayName = 'MenuModalContent'

function MenuItem({ className, children, active, isNew }: { className?: string; children: React.ReactNode; active?: boolean; isNew?: boolean }) {
  return (
    <div className={cn('flex items-center min-h-[64px]', className)}>
      <div className={cn('inline-flex items-center text-greyscale-300 typo-new-label-2', { 'text-greyscale-100': active })}>{children}</div>
      {isNew && <span className={'ml-2 inline-flex-center px-1 h-4 rounded-[4px] bg-primary-500 text-greyscale-100 typo-new-label-3'}>New</span>}
    </div>
  )
}
